import { useCallback, useEffect, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";

const PaymentSettings = () => {
  const [settings, setSettings] = useState(null);
  const [key, setKey] = useState("");
  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/payment-settings", { method: "GET" });
      if (response.data?.s === 200) {
        const paymentSettings = response.data.r || {};
        // Support the existing API response (KEY/SECRET) while the backend
        // transitions to the lower-case encrypted-settings response.
        setSettings({
          ...paymentSettings,
          key: paymentSettings.key || paymentSettings.KEY || "Not configured",
          secret: paymentSettings.secret || paymentSettings.SECRET || "Not configured",
          isConfigured: paymentSettings.isConfigured ?? Boolean(
            (paymentSettings.key || paymentSettings.KEY) &&
            (paymentSettings.secret || paymentSettings.SECRET)
          ),
        });
      } else if (response.data?.s === 404) {
        setSettings({ isConfigured: false });
      } else {
        throw new Error(response.data?.m || "Unable to load payment settings");
      }
    } catch (error) {
      setSnack({ message: error.message || "Unable to load payment settings", type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async (event) => {
    event.preventDefault();
    if (!key.trim() || !secret.trim()) {
      setSnack({ message: "Enter both the Razorpay Key ID and Key Secret", type: "error" });
      return;
    }

    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/payment-settings", {
        method: "PUT",
        body: JSON.stringify({ key: key.trim(), secret: secret.trim() }),
      });
      if (response.data?.s !== 200) {
        throw new Error(response.data?.m || "Unable to save payment settings");
      }

      setKey("");
      setSecret("");
      setSnack({ message: "Payment settings updated securely", type: "success" });
      await loadSettings();
    } catch (error) {
      setSnack({ message: error.message || "Unable to save payment settings", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Spinnerr show={loading} />
      {snack && <Toaster notification={snack} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-4" style={{ maxWidth: 720 }}>
        <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
          <div>
            <h2 className="mb-1">Payment Gateway</h2>
            <p className="text-muted mb-0">Configure Razorpay credentials used for new payments.</p>
          </div>
          <span className={`badge ${settings?.isConfigured ? "bg-success" : "bg-secondary"}`}>
            {settings?.isConfigured ? "Configured" : "Not configured"}
          </span>
        </div>

        {settings?.isConfigured && (
          <div className="border rounded p-3 bg-light mb-4">
            <div className="small text-muted mb-3">Current credentials (masked)</div>
            <div className="row g-3">
              <div className="col-md-6">
                <label htmlFor="current-razorpay-key" className="form-label">Current Key ID</label>
                <input
                  id="current-razorpay-key"
                  className="form-control"
                  value={settings.key || ""}
                  readOnly
                  aria-label="Current masked Razorpay Key ID"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="current-razorpay-secret" className="form-label">Current Key Secret</label>
                <input
                  id="current-razorpay-secret"
                  className="form-control"
                  value={settings.secret || ""}
                  readOnly
                  aria-label="Current masked Razorpay Key Secret"
                />
              </div>
            </div>
          </div>
        )}

        <form onSubmit={saveSettings}>
          <div className="mb-3">
            <label htmlFor="razorpay-key" className="form-label">New Razorpay Key ID</label>
            <input
              id="razorpay-key"
              className="form-control"
              type="password"
              autoComplete="off"
              value={key}
              onChange={(event) => setKey(event.target.value)}
              placeholder="Enter a new Key ID"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="razorpay-secret" className="form-label">New Razorpay Key Secret</label>
            <div className="input-group">
              <input
                id="razorpay-secret"
                className="form-control"
                type={showSecret ? "text" : "password"}
                autoComplete="new-password"
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
                placeholder="Enter a new Key Secret"
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                aria-label={showSecret ? "Hide key secret" : "Show key secret"}
                onClick={() => setShowSecret((visible) => !visible)}
              >
                {showSecret ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <p className="small text-muted">Credentials are encrypted before being stored. Existing values are never returned by this page.</p>
          <button className="btn btn-primary" type="submit" disabled={loading}>Save payment settings</button>
        </form>
      </div>
    </>
  );
};

export default PaymentSettings;
